import Controller from '@ember/controller';
import { service } from '@ember/service';

export default class EventClassgroupGraphController extends Controller {
  @service eventData;
  @service resultsCalculator;

  get results() {
    const { eventUuid, classGroupUuid } = this.model;
    return this.resultsCalculator.calculateResultsForClassGroup(eventUuid, classGroupUuid);
  }

  get classGroup() {
    const { eventUuid, classGroupUuid } = this.model;
    const eventDataObj = this.eventData.getEventData(eventUuid);
    return eventDataObj?.classGroups.get(classGroupUuid);
  }

  get course() {
    const { eventUuid, classGroupUuid } = this.model;
    return this.eventData.getCourseForClassGroup(eventUuid, classGroupUuid);
  }

  /**
   * Get timing point columns (public splits only) for the graph X-axis
   */
  get splitColumns() {
    const course = this.course;
    if (!course) return [];

    const courseElements = course.courseElements || course.CourseElements || [];
    return courseElements
      .filter(ce => ce.intermediateTimingPointUuid !== null && ce.isPublic)
      .sort((a, b) => a.order - b.order)
      .map(ce => ({
        name: ce.name || ce.intermediateTimingPointName,
        uuid: ce.uuid
      }));
  }

  /**
   * Calculate Superman splits - the fastest split time at each timing point
   * Matches splits by courseElementUuid, not by index
   */
  get supermanSplits() {
    const results = this.results;
    const splitColumns = this.splitColumns;

    if (!results || !splitColumns.length) return [];

    return splitColumns.map(col => {
      let fastestSplit = Infinity;

      results.forEach(result => {
        // Find split by courseElementUuid matching the column's uuid
        const split = result.splitsMap?.[col.uuid] ||
          result.splits?.find(s => s.courseElementUuid === col.uuid);

        if (split?.split && split.split > 0 && split.split < fastestSplit) {
          fastestSplit = split.split;
        }
      });

      return {
        name: col.name,
        uuid: col.uuid,
        split: fastestSplit === Infinity ? 0 : fastestSplit
      };
    });
  }

  /**
   * Calculate the fastest finish split (time from last timing point to finish)
   */
  get supermanFinishSplit() {
    const results = this.results;
    const splitColumns = this.splitColumns;

    if (!results || !splitColumns.length) return 0;

    const lastColumnUuid = splitColumns[splitColumns.length - 1]?.uuid;
    let fastestFinishSplit = Infinity;

    results.forEach(result => {
      // Only consider finished racers
      if (result.status !== 'OK' || !result.totalTimeInMilliseconds) return;

      // Find the last timing point split to get elapsed time there
      const lastSplit = result.splitsMap?.[lastColumnUuid] ||
        result.splits?.find(s => s.courseElementUuid === lastColumnUuid);

      if (lastSplit?.elapsed) {
        // Finish split = total time - elapsed at last timing point
        const finishSplit = result.totalTimeInMilliseconds - lastSplit.elapsed;
        if (finishSplit > 0 && finishSplit < fastestFinishSplit) {
          fastestFinishSplit = finishSplit;
        }
      }
    });

    return fastestFinishSplit === Infinity ? 0 : fastestFinishSplit;
  }

  /**
   * Calculate cumulative Superman time at each point
   */
  get supermanCumulativeTimes() {
    let cumulative = 0;
    return this.supermanSplits.map(s => {
      cumulative += s.split;
      return cumulative;
    });
  }

  /**
   * Superman's total time (cumulative splits + finish split)
   */
  get supermanTotalTime() {
    const lastCumulative = this.supermanCumulativeTimes[this.supermanCumulativeTimes.length - 1] || 0;
    return lastCumulative + this.supermanFinishSplit;
  }

  /**
   * Build graph data: for each racer, compute time behind Superman at each point
   * Uses elapsed times for comparison, which handles missing intermediates naturally
   * Line connects only the points where data exists (no dots for missing intermediates)
   */
  get graphData() {
    const supermanCumulative = this.supermanCumulativeTimes;
    const splitColumns = this.splitColumns;
    const supermanTotal = this.supermanTotalTime;

    if (!splitColumns.length) return [];

    return this.results
      .filter(r => r.status === 'OK' || (r.splits && r.splits.length > 0))
      .map(result => {
        const points = [];

        // Add start point (everyone at 0)
        points.push({
          timingPointIndex: -1,
          timingPointName: 'Start',
          behind: 0
        });

        // Calculate time behind at each public timing point where racer has data
        splitColumns.forEach((col, i) => {
          // Find this racer's split for this timing point by UUID
          const split = result.splitsMap?.[col.uuid] ||
            result.splits?.find(s => s.courseElementUuid === col.uuid);

          // Only add point if racer has elapsed time at this timing point
          if (split && split.elapsed) {
            // Compare racer's elapsed to Superman's cumulative elapsed at this point
            const supermanElapsed = supermanCumulative[i] || 0;
            const behind = split.elapsed - supermanElapsed;

            points.push({
              timingPointIndex: i,
              timingPointName: col.name,
              behind: behind
            });
          }
          // If no data for this timing point, skip it (no dot, line will connect adjacent points)
        });

        // Add finish point if racer has a finish time
        if (result.totalTimeInMilliseconds && result.status === 'OK') {
          const finishBehind = result.totalTimeInMilliseconds - supermanTotal;
          points.push({
            timingPointIndex: splitColumns.length,
            timingPointName: 'Finish',
            behind: finishBehind
          });
        }

        return {
          name: result.name || `${result.firstName} ${result.lastName}`.trim(),
          bib: result.bib,
          points,
          isFinished: result.status === 'OK',
          place: result.place
        };
      })
      .filter(r => r.points.length > 1); // Only include racers with at least start + one timing point
  }

  /**
   * X-axis labels for the graph
   */
  get xAxisLabels() {
    const labels = ['Start'];
    this.splitColumns.forEach(col => labels.push(col.name));
    labels.push('Finish');
    return labels;
  }

  get snapshotComplete() {
    const { eventUuid } = this.model;
    const eventDataObj = this.eventData.getEventData(eventUuid);
    return eventDataObj?.snapshotComplete || false;
  }

  get eventName() {
    const { eventUuid } = this.model;
    const eventDataObj = this.eventData.getEventData(eventUuid);
    return eventDataObj?.event?.name || 'Loading...';
  }

  get pageTitle() {
    return `${this.classGroup?.name || 'Results'} - Graph`;
  }
}
